import Ad from "@components/Advertisement/Ad";
import { KeywordButtonType } from "@components/Button/utils/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import KeywordFilter from "@features/mybt/components/KeywordFilter";
import { KeywordType } from "@features/mybt/components/KeywordFilter/utils/types";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import Listing from "@particles/listing/Listing";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import AsiaFutureSummitLogo from "@routes/Keyword/templates/TemplateKeywordDefault/components/AsiaFutureSummitLogo";
import { DefaultTemplateData } from "@routes/Keyword/utils/types";
import BreakingNews from "@routes/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@routes/Section/components/MostPopular/MostPopular";
import { ASIA_FUTURE_SUMMIT_URL, sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

/**
 * Default template for Keyword pages.
 *
 * @param {TemplateKeywordDefaultProps} props - The properties for the template.
 * @returns {React.ReactElement} The rendered Keyword default template component.
 */
const TemplateKeywordDefault = ({
  entity: { tag },
  articles,
  seoFooterData,
}: DefaultTemplateData): React.ReactElement => {
  const breakingNews = useTrimmedBreakingNews();
  const mostPopular = useMostReadArticleList();

  const relativePath = getUrlPathFromAbsoluteUrl(tag.urlPath);

  return (
    <div className="w-full" data-testid="template-keyword-default-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        parentCategory="keywords"
        childCategory={relativePath.replace("/keywords/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <PulseEntry />

      <Container rootClassName="pt-6">
        {tag.urlPath === ASIA_FUTURE_SUMMIT_URL ? (
          <AsiaFutureSummitLogo
            className={cn("mb-6 md:border-b md:border-gray-850 md:py-4")}
          />
        ) : (
          <KeywordFilter
            keywordButtonType={KeywordButtonType.version2}
            tag={tag}
            className={cn(
              "mb-6 inline-block w-full border-b border-gray-850 py-3 pt-6"
            )}
            tagClassName="border-none !font-playfair !text-6xl md:!text-8xl !p-0 text-gray-850 !uppercase !text-left !text-normal"
            toolTipClassName={
              "!right-0 top-full min-[425px]:!left-auto min-[425px]:top-1/2 min-[425px]:flex min-[425px]:-translate-y-1/2 min-[425px]:-translate-x-0 min-[425px]:items-center xl:left-auto xl:top-full xl:block xl:-translate-x-0 xl:translate-y-0 top-full min-[425px]:top-full min-[425px]:!block left-auto w-[200px] xs:w-[250px] xl:translate-x-0"
            }
            toolTipArrowClassName={
              "xl:ml-0 xl:mr-10 md:mr-10 border-b-[8px] border-l-[6px] border-r-[6px] border-x-transparent border-b-black mr-3 min-[425px]:border-r-transparent min-[425px]:!border-b-black min-[425px]:border-t-transparent relative left-[90%] md:left-[80%] mr-0 border-t-transparent ml-0"
            }
            disableTooltip
            keywordType={KeywordType.listing}
            buttonClassName={"lg:ml-4"}
          />
        )}

        <div className="grid lg:grid-cols-12">
          <div className="mb-6 lg:col-span-8">
            <Listing
              cardProps={articles}
              urlPath={tag.urlPath}
              pageType={"keyword"}
              size={10}
              isUseTemplateH
            />
          </div>

          <div className="mb-6 lg:col-span-4 lg:pl-6">
            <div className="mb-6 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-[60px]"
              />
            </div>

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />

            <MostPopular
              data={mostPopular}
              rootClassName="max-w-[300px] mx-auto"
            />
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default TemplateKeywordDefault;
